/**
 * HomeTab — Personal HQ landing page.
 *
 * Composable via PageBuilder. Default layout includes:
 *   My Tasks  |  Inbox
 *   Recent Notes | Quick Actions | Mini Calendar
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageBuilder } from '../../widget-framework/PageBuilder';
import { ConfigPanel } from '../../widget-framework/ConfigPanel';
import { storageGetJSON, storageSetJSON } from '../../core/api/client';
import { STORAGE_KEYS } from '../../core/utils/defaults';
import { getPresetTabLayout } from '../../widget-framework/presets';
import { useAppStore } from '../../core/store/appStore';
import type { TabLayout, WidgetGridItem } from '../../core/types';

const TAB_ID = 'home';

export function HomeTab() {
  const queryClient = useQueryClient();
  const { context, settings, configPanelInstanceId, setConfigPanel } = useAppStore();
  const userId = context?.user?.id ?? null;
  const storageKey = userId ? STORAGE_KEYS.layout(userId, TAB_ID) : null;

  // Load layout from monday Storage
  const { data: layout } = useQuery<TabLayout>({
    queryKey: ['tab_layout', TAB_ID, userId],
    queryFn: async () => {
      if (!storageKey) return getPresetTabLayout('default', TAB_ID);
      const stored = await storageGetJSON<TabLayout>(storageKey);
      return stored ?? getPresetTabLayout(settings?.activePreset ?? 'default', TAB_ID);
    },
    enabled: Boolean(userId),
    staleTime: Infinity,
  });

  // Persist layout changes
  const saveMutation = useMutation({
    mutationFn: (updated: TabLayout) => {
      if (!storageKey) return Promise.resolve();
      return storageSetJSON(storageKey, updated);
    },
    onSuccess: (_, updated) => {
      queryClient.setQueryData(['tab_layout', TAB_ID, userId], updated);
    },
  });

  const handleLayoutChange = useCallback(
    (updated: TabLayout) => saveMutation.mutate(updated),
    [saveMutation]
  );

  const handleOpenConfig = useCallback(
    (instanceId: string) => {
      const item = layout?.widgets.find((w) => w.instanceId === instanceId);
      if (item) setConfigPanel(instanceId, item.widgetId);
    },
    [layout, setConfigPanel]
  );

  const handleApplyConfig = useCallback(
    (instanceId: string, newConfig: Record<string, unknown>) => {
      if (!layout) return;
      const updated: TabLayout = {
        ...layout,
        widgets: layout.widgets.map((w): WidgetGridItem =>
          w.instanceId === instanceId ? { ...w, config: newConfig } : w
        ),
      };
      saveMutation.mutate(updated);
    },
    [layout, saveMutation]
  );

  // Fall back to default preset when storage layout is unavailable (e.g. userId null)
  const effectiveLayout = useMemo(
    () => layout ?? getPresetTabLayout('default', TAB_ID),
    [layout],
  );

  if (!context) {
    return <div className="tab-loading" aria-label="Loading Home" />;
  }

  const configItem = configPanelInstanceId
    ? effectiveLayout.widgets.find((w) => w.instanceId === configPanelInstanceId)
    : null;

  return (
    <div className="tab-root tab-root--home">
      <PageBuilder
        tab={{ id: TAB_ID, label: 'Home', icon: '🏠' }}
        layout={effectiveLayout}
        surface="custom_object"
        context={context}
        onLayoutChange={handleLayoutChange}
        onOpenConfig={handleOpenConfig}
      />
      <ConfigPanel
        instanceId={configPanelInstanceId ?? null}
        widgetId={configItem?.widgetId ?? null}
        currentConfig={configItem?.config ?? {}}
        onApply={handleApplyConfig}
        onClose={() => setConfigPanel(null, null)}
      />
    </div>
  );
}
